import { useEffect } from 'react'
import { create } from 'zustand'
import { useTenantStore } from '@/stores/tenantStore'

export interface DataStoreState<T> {
  data: T | undefined
  loading: boolean
  error: Error | null
  currentTenantId: string | null
  fetchPromise: Promise<void> | null
  setData: (data: T | undefined) => void
  setLoading: (loading: boolean) => void
  setError: (error: Error | null) => void
  fetchData: (tenantId: string, queryFn: () => Promise<any>) => Promise<void>
  clearData: () => void
}

export function createDataStore<T>(options?: {
  dataTransformer?: (data: any) => T
}) {
  return create<DataStoreState<T>>((set, get) => ({
    data: undefined,
    loading: true,
    error: null,
    currentTenantId: null,
    fetchPromise: null,

    setData: (data) => set({ data }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),

    fetchData: async (tenantId: string, queryFn: () => Promise<any>) => {
      const state = get()

      // Return existing promise if fetch is already in progress for this tenant
      if (state.fetchPromise && state.currentTenantId === tenantId) {
        return state.fetchPromise
      }

      // Don't fetch if we already have data for this tenant
      if (state.data && state.currentTenantId === tenantId && !state.error) {
        return
      }

      const fetchPromise = (async () => {
        try {
          set({ loading: true, error: null, currentTenantId: tenantId })

          const { data } = await queryFn()

          // Check if this fetch is still relevant (tenant hasn't changed)
          const currentState = get()
          if (currentState.currentTenantId === tenantId) {
            const transformedData = options?.dataTransformer
              ? options.dataTransformer(data)
              : data

            set({
              data: transformedData,
              loading: false,
              error: null,
            })
          }
        } catch (err) {
          const currentState = get()
          if (currentState.currentTenantId === tenantId) {
            set({
              loading: false,
              error:
                err instanceof Error ? err : new Error('Failed to fetch data'),
            })
          }
        } finally {
          const currentState = get()
          if (currentState.currentTenantId === tenantId) {
            set({ fetchPromise: null })
          }
        }
      })()

      set({ fetchPromise })
      return fetchPromise
    },

    clearData: () => {
      set({
        data: undefined,
        loading: true,
        error: null,
        currentTenantId: null,
        fetchPromise: null,
      })
    },
  }))
}

export function createDataHooks<T>(
  useStore: () => DataStoreState<T>,
  useQuery: () => [queryFn: () => Promise<any>, { loading?: boolean }]
) {
  function useData() {
    const { data, loading, error, fetchData } = useStore()
    const { selectedTenantId } = useTenantStore()

    const [query] = useQuery()

    useEffect(() => {
      if (!selectedTenantId || selectedTenantId === '') return

      fetchData(selectedTenantId, query)
    }, [selectedTenantId, query, fetchData])

    return {
      data,
      loading,
      error,
    }
  }

  function useClearData() {
    const { clearData } = useStore()
    return clearData
  }

  function useUpdateData() {
    const { setData, setLoading, setError } = useStore()
    return (data: T) => {
      setData(data)
      setLoading(false)
      setError(null)
    }
  }

  return {
    useData,
    useClearData,
    useUpdateData,
  }
}
